import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Settings as SettingsIcon, Mail, Bell, Send, CheckCircle2, XCircle, Clock, Eye, AlertCircle, RefreshCw, Inbox, Save, Users, FileText, RotateCcw, MousePointerClick, User, Activity, Calendar, Filter, ChevronRight, Book } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import Emails from './Emails';
import Documentation from './Documentation';
import { useAuth } from '../../contexts/AuthContext';

// Lazy load the editors
const RichTextEditor = lazy(() => import('../ui/RichTextEditor'));
const VisualEmailEditor = lazy(() => import('../ui/VisualEmailEditor'));

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Profile state
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    sender_name: 'PPMT Strategic Group',
    sender_email: '',
    default_cc: '',
    default_bcc: '',
    reply_to: '',
    admin_notification_email: '',
    send_copy_to_admin: false
  });

  // Notification templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Activity logs state
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [logFilters, setLogFilters] = useState({
    startDate: '',
    endDate: '',
    activityType: 'all',
    timeframe: '7days'
  });

  // Initialize profile data from user
  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  useEffect(() => {
    fetchEmailConfig();
    fetchEmailStats();
    fetchNotifications();
    fetchEmailSettings();
    fetchTemplates();
    fetchActivityTypes();
  }, []);

  const fetchEmailConfig = async () => {
    try {
      const response = await api.get('/email/config');
      setEmailConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch email config:', error);
    } finally {
      setLoadingConfig(false);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await api.get('/email/settings');
      setEmailSettings({
        sender_name: response.data.sender_name || 'PPMT Strategic Group',
        sender_email: response.data.sender_email || '',
        default_cc: response.data.default_cc || '',
        default_bcc: response.data.default_bcc || '',
        reply_to: response.data.reply_to || '',
        admin_notification_email: response.data.admin_notification_email || '',
        send_copy_to_admin: response.data.send_copy_to_admin || false
      });
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/notification-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchEmailStats = async () => {
    try {
      const response = await api.get('/email-notifications/stats');
      setEmailStats(response.data);
    } catch (error) {
      console.error('Failed to fetch email stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/email-notifications?limit=100');
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/email/settings', emailSettings);
      toast.success('Email settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save email settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchActivityTypes = async () => {
    try {
      const response = await api.get('/admin/activity-logs/types');
      setActivityTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch activity types:', error);
    }
  };

  const fetchActivityLogs = async () => {
    setLoadingLogs(true);
    try {
      let params = new URLSearchParams();
      
      // Calculate dates based on timeframe
      const now = new Date();
      let startDate = logFilters.startDate;
      let endDate = logFilters.endDate;
      
      if (logFilters.timeframe !== 'custom') {
        endDate = now.toISOString().split('T')[0];
        const daysMap = {
          'today': 0,
          '7days': 7,
          '30days': 30,
          '90days': 90,
          'all': 365 * 10
        };
        const daysAgo = new Date(now);
        daysAgo.setDate(daysAgo.getDate() - (daysMap[logFilters.timeframe] || 7));
        startDate = daysAgo.toISOString().split('T')[0];
      }
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (logFilters.activityType && logFilters.activityType !== 'all') {
        params.append('activity_type', logFilters.activityType);
      }
      params.append('limit', '200');
      
      const response = await api.get(`/admin/activity-logs?${params.toString()}`);
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      client_archived: '📦',
      client_deleted: '🗑️',
      client_suspended: '⏸️',
      client_revoked: '🚫',
      client_reactivated: '✅',
      client_restored: '♻️',
      bulk_archive: '📦',
      bulk_delete: '🗑️',
      bulk_suspend: '⏸️',
      password_reset_sent: '🔑',
      password_changed: '🔐',
      team_member_added: '👥',
      file_uploaded: '📄',
      message_received: '💬',
      sign_in: '🔐',
      signature_signed: '✍️',
      signature_pending: '⏳'
    };
    return icons[activityType] || '📝';
  };

  const getActivityColor = (activityType) => {
    const colors = {
      client_archived: 'bg-orange-100 text-orange-800',
      client_deleted: 'bg-red-100 text-red-800',
      client_suspended: 'bg-yellow-100 text-yellow-800',
      client_revoked: 'bg-red-100 text-red-800',
      client_reactivated: 'bg-green-100 text-green-800',
      client_restored: 'bg-green-100 text-green-800',
      bulk_archive: 'bg-orange-100 text-orange-800',
      bulk_delete: 'bg-red-100 text-red-800',
      password_reset_sent: 'bg-blue-100 text-blue-800',
      password_changed: 'bg-purple-100 text-purple-800',
      team_member_added: 'bg-purple-100 text-purple-800',
      file_uploaded: 'bg-blue-100 text-blue-800',
      sign_in: 'bg-green-100 text-green-800',
      signature_signed: 'bg-teal-100 text-teal-800'
    };
    return colors[activityType] || 'bg-gray-100 text-gray-800';
  };

  const formatActivityType = (type) => {
    if (!type) return 'Unknown';
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSavingTemplate(true);
    try {
      await api.put(`/notification-templates/${editingTemplate.template_key}`, {
        name: editingTemplate.name,
        subject: editingTemplate.subject,
        html_body: editingTemplate.html_body
      });
      toast.success('Template saved successfully!');
      fetchTemplates();
      setSelectedTemplate(null);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async (templateKey) => {
    try {
      await api.post(`/notification-templates/reset/${templateKey}`);
      toast.success('Template reset to default!');
      fetchTemplates();
      if (editingTemplate?.template_key === templateKey) {
        const response = await api.get(`/notification-templates/${templateKey}`);
        setEditingTemplate(response.data);
      }
    } catch (error) {
      console.error('Failed to reset template:', error);
      toast.error('Failed to reset template');
    }
  };

  const handleSaveProfile = async () => {
    if (!profileData.first_name || !profileData.last_name || !profileData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingProfile(true);
    try {
      const response = await api.put('/auth/profile', profileData);
      if (updateUser) {
        updateUser(response.data);
      }
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await api.put('/auth/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingTest(true);
    try {
      const response = await api.post('/email/test', { to_email: testEmail });
      if (response.data.success) {
        toast.success('Test email sent successfully!');
        setTestEmail('');
        fetchNotifications();
        fetchEmailStats();
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Send className="w-4 h-4 text-blue-500" />;
      case 'delivered':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'opened':
        return <Eye className="w-4 h-4 text-purple-500" />;
      case 'clicked':
        return <MousePointerClick className="w-4 h-4 text-indigo-500" />;
      case 'failed':
      case 'bounced':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-blue-100 text-blue-700';
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'opened':
        return 'bg-purple-100 text-purple-700';
      case 'clicked':
        return 'bg-indigo-100 text-indigo-700';
      case 'failed':
      case 'bounced':
        return 'bg-red-100 text-red-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTemplateTypeLabel = (key) => {
    switch (key) {
      case 'client_invitation':
        return 'Portal Invitation';
      case 'document_shared':
        return 'Document Notification';
      case 'new_message':
        return 'Message Notification';
      default:
        return key;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="settings-title">Settings</h2>
        <p className="text-gray-600">Manage system settings and configurations</p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:w-auto md:inline-grid md:grid-cols-8 gap-1">
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox">
            <Inbox className="w-4 h-4 mr-2" />
            Inbox
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <FileText className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="email-settings" data-testid="tab-email-settings">
            <Mail className="w-4 h-4 mr-2" />
            Email Settings
          </TabsTrigger>
          <TabsTrigger value="email-config" data-testid="tab-email-config">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Config
          </TabsTrigger>
          <TabsTrigger value="compose" data-testid="tab-compose">
            <Send className="w-4 h-4 mr-2" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="activity-logs" data-testid="tab-activity-logs" onClick={() => fetchActivityLogs()}>
            <Activity className="w-4 h-4 mr-2" />
            Activity Logs
          </TabsTrigger>
          <TabsTrigger value="documentation" data-testid="tab-documentation">
            <Book className="w-4 h-4 mr-2" />
            Help
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Profile Information */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-teal-600" />
                Profile Information
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      data-testid="profile-first-name"
                      value={profileData.first_name}
                      onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      data-testid="profile-last-name"
                      value={profileData.last_name}
                      onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="profile-email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    placeholder="Enter email address"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    data-testid="profile-phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </div>
                
                <Button 
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                  data-testid="save-profile-btn"
                >
                  {savingProfile ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Profile
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Change Password */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <SettingsIcon className="w-5 h-5 mr-2 text-teal-600" />
                Change Password
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="current_password">Current Password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    data-testid="current-password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Enter current password"
                  />
                </div>
                
                <div>
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    data-testid="new-password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Enter new password"
                  />
                </div>
                
                <div>
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    data-testid="confirm-password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                  />
                </div>
                
                <Button 
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  variant="outline"
                  className="w-full"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Email Inbox Tab */}
        <TabsContent value="inbox" className="mt-6">
          <div className="glass rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <h3 className="font-bold text-gray-900">Email Inbox</h3>
                {emailStats && (
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">{emailStats.sent} Sent</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">{emailStats.delivered} Delivered</span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">{emailStats.opened} Opened</span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full">{emailStats.failed} Failed</span>
                  </div>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={fetchNotifications}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            {loadingNotifications ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-teal-600" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No emails sent yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {notifications.map((notification) => (
                      <tr key={notification.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedNotification(notification)}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(notification.status)}`}>
                            {getStatusIcon(notification.status)}
                            <span className="ml-1 capitalize">{notification.status}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{notification.to}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{notification.subject}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs capitalize">
                            {notification.template_type || 'custom'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {notification.sent_at ? new Date(notification.sent_at).toLocaleString() : 
                           notification.created_at ? new Date(notification.created_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedNotification(notification);
                            }}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Notification Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Template List */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-teal-600" />
                Notification Templates
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                These templates are used for automated notifications sent to clients.
              </p>
              
              {loadingTemplates ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse bg-gray-100 rounded-lg p-4 h-16"></div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map((template) => (
                    <div
                      key={template.template_key}
                      className={`p-4 rounded-lg transition-all ${
                        selectedTemplate?.template_key === template.template_key
                          ? 'bg-teal-50 border-2 border-teal-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      } ${template.is_active === false ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setEditingTemplate({ ...template });
                          }}
                        >
                          <p className="font-medium text-gray-900">{template.name}</p>
                          <p className="text-xs text-gray-500">{getTemplateTypeLabel(template.template_key)}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                          <Switch
                            checked={template.is_active !== false}
                            onCheckedChange={async () => {
                              try {
                                await api.put(`/notification-templates/${template.template_key}/toggle`);
                                toast.success(`Template ${template.is_active === false ? 'enabled' : 'disabled'}`);
                                fetchTemplates();
                              } catch (error) {
                                toast.error('Failed to toggle template');
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Template Editor */}
            <div className="lg:col-span-2 glass rounded-xl p-6">
              {editingTemplate ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-gray-900">Edit Template</h3>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetTemplate(editingTemplate.template_key)}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reset
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="template-name">Template Name</Label>
                    <Input
                      id="template-name"
                      value={editingTemplate.name}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setEditingTemplate(prev => ({ ...prev, name: newValue }));
                      }}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="template-subject">Email Subject</Label>
                    <Input
                      id="template-subject"
                      value={editingTemplate.subject}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setEditingTemplate(prev => ({ ...prev, subject: newValue }));
                      }}
                      className="mt-1"
                    />
                  </div>

                  {editingTemplate.placeholders && editingTemplate.placeholders.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <Label className="text-blue-800 mb-2 block">Available Placeholders</Label>
                      <div className="flex flex-wrap gap-2">
                        {editingTemplate.placeholders.map((placeholder) => (
                          <code
                            key={placeholder}
                            className="px-2 py-1 bg-white text-blue-700 rounded text-xs font-mono cursor-pointer hover:bg-blue-100"
                            onClick={() => {
                              navigator.clipboard.writeText(`[${placeholder}]`);
                              toast.success(`Copied [${placeholder}]`);
                            }}
                          >
                            [{placeholder}]
                          </code>
                        ))}
                      </div>
                      <p className="text-xs text-blue-600 mt-2">Click to copy a placeholder</p>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="template-body">Email Body</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Use the toolbar to format text. Use "Insert Variable" to add dynamic placeholders.
                    </p>
                    <div className="mt-1">
                      <Suspense fallback={<div className="h-64 bg-gray-100 rounded-lg animate-pulse" />}>
                        <VisualEmailEditor
                          value={editingTemplate.html_body}
                          onChange={(value) => setEditingTemplate(prev => ({ ...prev, html_body: value }))}
                          placeholder="Write your email template here..."
                          height="250px"
                          placeholders={editingTemplate.placeholders || []}
                        />
                      </Suspense>
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveTemplate}
                    disabled={savingTemplate}
                    className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                  >
                    {savingTemplate ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Template
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Select a template to edit</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="email-settings" className="mt-6">
          <div className="glass rounded-xl p-6 max-w-2xl">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center">
              <Mail className="w-5 h-5 mr-2 text-teal-600" />
              Outgoing Email Settings
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Configure default settings for all outgoing emails from the portal.
            </p>
            
            <div className="space-y-6">
              {/* Sender Identity Section */}
              <div className="border-b pb-6">
                <h4 className="font-medium text-gray-800 mb-4">Sender Identity</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sender-name">Sender Name</Label>
                    <p className="text-xs text-gray-500 mb-1">Display name that appears in the "From" field</p>
                    <Input
                      id="sender-name"
                      placeholder="PPMT Strategic Group"
                      value={emailSettings.sender_name}
                      onChange={(e) => setEmailSettings({ ...emailSettings, sender_name: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sender-email">From Email</Label>
                    <p className="text-xs text-gray-500 mb-1">Must be authorized in Mailgun</p>
                    <Input
                      id="sender-email"
                      type="email"
                      placeholder="noreply@portal.ppmtgroup.com"
                      value={emailSettings.sender_email}
                      onChange={(e) => setEmailSettings({ ...emailSettings, sender_email: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Reply & Copy Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800">Reply & Copy Settings</h4>
                
                {/* Reply-To */}
                <div>
                  <Label htmlFor="reply-to">Reply-To Email</Label>
                  <p className="text-xs text-gray-500 mb-1">When clients reply to emails, responses will be sent to this address</p>
                  <Input
                    id="reply-to"
                    type="email"
                    placeholder="e.g., support@ppmtgroup.com"
                    value={emailSettings.reply_to}
                    onChange={(e) => setEmailSettings({ ...emailSettings, reply_to: e.target.value })}
                  />
                </div>

                {/* Default CC */}
                <div>
                  <Label htmlFor="default-cc">Default CC</Label>
                  <p className="text-xs text-gray-500 mb-1">This email will be CC'd on all outgoing emails</p>
                  <Input
                    id="default-cc"
                    type="email"
                    placeholder="e.g., records@ppmtgroup.com"
                    value={emailSettings.default_cc}
                    onChange={(e) => setEmailSettings({ ...emailSettings, default_cc: e.target.value })}
                  />
                </div>

                {/* Default BCC */}
                <div>
                  <Label htmlFor="default-bcc">Default BCC</Label>
                  <p className="text-xs text-gray-500 mb-1">This email will be BCC'd on all outgoing emails (hidden from recipients)</p>
                  <Input
                    id="default-bcc"
                    type="email"
                    placeholder="e.g., archive@ppmtgroup.com"
                    value={emailSettings.default_bcc}
                    onChange={(e) => setEmailSettings({ ...emailSettings, default_bcc: e.target.value })}
                  />
                </div>
              </div>

              {/* Admin Notification Settings */}
              <div className="border-t pt-6 space-y-4">
                <h4 className="font-medium text-gray-800 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Admin Notification Settings
                </h4>
                
                <div>
                  <Label htmlFor="admin-notification">Admin Notification Email</Label>
                  <p className="text-xs text-gray-500 mb-1">Receive copies of all portal notifications at this additional email address</p>
                  <Input
                    id="admin-notification"
                    type="email"
                    placeholder="e.g., admin@ppmtgroup.com"
                    value={emailSettings.admin_notification_email}
                    onChange={(e) => setEmailSettings({ ...emailSettings, admin_notification_email: e.target.value })}
                  />
                </div>

                {/* Enable Admin Copy */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="send-copy" className="mb-0">Send Copy to Admin</Label>
                    <p className="text-xs text-gray-500">When enabled, a copy of all emails will be sent to the admin notification email above</p>
                  </div>
                  <Switch
                    id="send-copy"
                    checked={emailSettings.send_copy_to_admin}
                    onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, send_copy_to_admin: checked })}
                  />
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
              >
                {savingSettings ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Email Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Email Configuration Tab */}
        <TabsContent value="email-config" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configuration Status */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <SettingsIcon className="w-5 h-5 mr-2 text-teal-600" />
                Email Service Configuration
              </h3>
              
              {loadingConfig ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ) : emailConfig ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Status</span>
                    <span className={`flex items-center ${emailConfig.configured ? 'text-green-600' : 'text-red-600'}`}>
                      {emailConfig.configured ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Connected
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-1" />
                          Not Configured
                        </>
                      )}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Provider</span>
                    <span className="font-medium text-gray-900">Mailgun</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Domain</span>
                    <span className="font-mono text-sm text-gray-900">{emailConfig.domain}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">From Email</span>
                    <span className="text-sm text-gray-900">{emailConfig.from_email}</span>
                  </div>
                  
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-700 font-medium">Portal URL in Emails</span>
                    </div>
                    <p className="text-sm text-blue-900 font-mono mt-1 break-all">
                      {emailConfig.portal_url || 'Not configured'}
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      This URL is used in all email notification links (password resets, invitations, etc.)
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Failed to load configuration</p>
              )}
            </div>

            {/* Test Email */}
            <div className="glass rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Send className="w-5 h-5 mr-2 text-teal-600" />
                Send Test Email
              </h3>
              
              <p className="text-sm text-gray-600 mb-4">
                Send a test email to verify your email configuration is working correctly.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="test-email">Recipient Email</Label>
                  <Input
                    id="test-email"
                    type="email"
                    placeholder="Enter email address"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <Button
                  onClick={handleSendTestEmail}
                  disabled={sendingTest || !emailConfig?.configured}
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                >
                  {sendingTest ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Test Email
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Email Statistics */}
            <div className="glass rounded-xl p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-teal-600" />
                  Email Statistics
                </h3>
                <Button variant="ghost" size="sm" onClick={fetchEmailStats}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              {loadingStats ? (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse bg-gray-100 rounded-lg p-4 h-20"></div>
                  ))}
                </div>
              ) : emailStats ? (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-gray-900">{emailStats.total}</p>
                    <p className="text-sm text-gray-500">Total</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{emailStats.sent}</p>
                    <p className="text-sm text-blue-600">Sent</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{emailStats.delivered}</p>
                    <p className="text-sm text-green-600">Delivered</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{emailStats.opened}</p>
                    <p className="text-sm text-purple-600">Opened</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{emailStats.failed}</p>
                    <p className="text-sm text-red-600">Failed</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No statistics available</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Compose Email Tab */}
        <TabsContent value="compose" className="mt-6">
          <Emails />
        </TabsContent>

        {/* Activity Logs Tab */}
        <TabsContent value="activity-logs" className="mt-6">
          <div className="glass rounded-xl p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <h3 className="font-bold text-gray-900 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-teal-600" />
                Activity Logs
              </h3>
              
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Timeframe Selector */}
                <Select 
                  value={logFilters.timeframe} 
                  onValueChange={(value) => {
                    setLogFilters(prev => ({ ...prev, timeframe: value }));
                    if (value !== 'custom') {
                      setTimeout(() => fetchActivityLogs(), 100);
                    }
                  }}
                >
                  <SelectTrigger className="w-[140px]" data-testid="timeframe-select">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Timeframe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="7days">Last 7 Days</SelectItem>
                    <SelectItem value="30days">Last 30 Days</SelectItem>
                    <SelectItem value="90days">Last 90 Days</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>

                {/* Custom Date Range */}
                {logFilters.timeframe === 'custom' && (
                  <>
                    <Input
                      type="date"
                      value={logFilters.startDate}
                      onChange={(e) => setLogFilters(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-[140px]"
                      data-testid="start-date-input"
                    />
                    <span className="text-gray-500">to</span>
                    <Input
                      type="date"
                      value={logFilters.endDate}
                      onChange={(e) => setLogFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-[140px]"
                      data-testid="end-date-input"
                    />
                  </>
                )}

                {/* Activity Type Filter */}
                <Select 
                  value={logFilters.activityType} 
                  onValueChange={(value) => {
                    setLogFilters(prev => ({ ...prev, activityType: value }));
                    setTimeout(() => fetchActivityLogs(), 100);
                  }}
                >
                  <SelectTrigger className="w-[180px]" data-testid="activity-type-select">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Activities</SelectItem>
                    {activityTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {formatActivityType(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Refresh Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchActivityLogs}
                  disabled={loadingLogs}
                  data-testid="refresh-logs-button"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingLogs ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Activity Logs List */}
            {loadingLogs ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              </div>
            ) : activityLogs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No activity logs found for the selected timeframe</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {activityLogs.map((log, index) => (
                  <div
                    key={log.id || index}
                    className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`activity-log-${index}`}
                  >
                    {/* Icon */}
                    <div className="text-2xl flex-shrink-0">
                      {getActivityIcon(log.action_type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-gray-900">{log.target_name || formatActivityType(log.action_type)}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActivityColor(log.action_type)}`}>
                          {formatActivityType(log.action_type)}
                        </span>
                        {log.source === 'admin' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Admin Action
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{log.details || ''}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {log.created_at ? new Date(log.created_at).toLocaleString() : 'Unknown date'}
                        </span>
                        {log.user_name && (
                          <span className="flex items-center">
                            <User className="w-3 h-3 mr-1" />
                            {log.user_name}
                          </span>
                        )}
                        {log.client_name && log.source === 'client' && (
                          <span className="flex items-center">
                            <Users className="w-3 h-3 mr-1" />
                            {log.client_name}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Arrow */}
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            {activityLogs.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-500">
                Showing {activityLogs.length} activities
              </div>
            )}
          </div>
        </TabsContent>

        {/* Documentation Tab */}
        <TabsContent value="documentation" className="mt-6">
          <Documentation />
        </TabsContent>
      </Tabs>

      {/* Notification Detail Dialog */}
      <Dialog open={!!selectedNotification} onOpenChange={() => setSelectedNotification(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
          </DialogHeader>
          
          {selectedNotification && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <p className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(selectedNotification.status)}`}>
                    {getStatusIcon(selectedNotification.status)}
                    <span className="ml-1 capitalize">{selectedNotification.status}</span>
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Type</Label>
                  <p className="text-gray-900 capitalize">{selectedNotification.template_type || 'Custom'}</p>
                </div>
              </div>
              
              {selectedNotification.from && (
                <div>
                  <Label className="text-gray-500">From</Label>
                  <p className="text-gray-900">{selectedNotification.from}</p>
                </div>
              )}
              
              <div>
                <Label className="text-gray-500">To</Label>
                <p className="text-gray-900">{selectedNotification.to}</p>
              </div>
              
              {selectedNotification.cc && (
                <div>
                  <Label className="text-gray-500">CC</Label>
                  <p className="text-gray-900">{Array.isArray(selectedNotification.cc) ? selectedNotification.cc.join(', ') : selectedNotification.cc}</p>
                </div>
              )}
              
              {selectedNotification.bcc && (
                <div>
                  <Label className="text-gray-500">BCC</Label>
                  <p className="text-gray-900">{Array.isArray(selectedNotification.bcc) ? selectedNotification.bcc.join(', ') : selectedNotification.bcc}</p>
                </div>
              )}
              
              {selectedNotification.reply_to && (
                <div>
                  <Label className="text-gray-500">Reply-To</Label>
                  <p className="text-gray-900">{selectedNotification.reply_to}</p>
                </div>
              )}
              
              <div>
                <Label className="text-gray-500">Subject</Label>
                <p className="text-gray-900">{selectedNotification.subject}</p>
              </div>
              
              {selectedNotification.html_body && (
                <div>
                  <Label className="text-gray-500 mb-2 block">Email Content</Label>
                  <div 
                    className="border rounded-lg p-4 bg-white max-h-60 overflow-y-auto text-sm"
                    dangerouslySetInnerHTML={{ __html: selectedNotification.html_body }}
                  />
                </div>
              )}
              
              {selectedNotification.events && selectedNotification.events.length > 0 && (
                <div>
                  <Label className="text-gray-500 mb-2 block">Event Timeline</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedNotification.events.map((event, index) => (
                      <div key={index} className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg">
                        {getStatusIcon(event.type)}
                        <div className="flex-1">
                          <p className="text-sm font-medium capitalize">{event.type}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(event.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                <div>
                  <Label className="text-gray-500">Created</Label>
                  <p className="text-gray-600">
                    {selectedNotification.created_at ? new Date(selectedNotification.created_at).toLocaleString() : '-'}
                  </p>
                </div>
                {selectedNotification.delivered_at && (
                  <div>
                    <Label className="text-gray-500">Delivered</Label>
                    <p className="text-gray-600">{new Date(selectedNotification.delivered_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
